import { useEffect, useState } from "react";
import axios from "axios";
import "./OnBoardingModule.css";
import { backendUrl } from "../../config";
import FileUpload from "../../components/FileUpload";
import InputField from "../../components/InputField";
import SectionHeader from "../../components/SectionHeader";
import SelectField from "../../components/SelectField";
import TextareaField from "../../components/TextareaField";
import searchIcon from "../../assets/icons/search.svg";
import checkIcon from "../../assets/icons/check.svg";
import loaderIcon from "../../assets/icons/loader.svg";
import refreshIcon from "../../assets/icons/refresh.svg";
import editIcon from "../../assets/icons/edit.svg";

const initialFormData = {
  firstName: "",
  lastName: "",
  fatherName: "",
  email: "",
  mobileNumber: "",
  gender: "",
  dateOfBirth: "",
  dateOfJoining: "",
  department: "",
  designation: "",
  reportingManager: "",
  experienceYears: "0",
  experienceMonths: "0",
  workLocation: "",
  emergencyContactNumber: "",
  emergencyContactPersonName: "",
  emergencyContactPersonRelation: "",
  permanentAddress: "",
  communicationAddress: "",
  maritalStatus: "",
  bloodGroup: "",
  bankAccountNumber: "",
  bankName: "",
  ifscCode: "",
  uanNumber: "",
  esicNumber: "",
  panNumber: "",
  aadhaarNumber: "",
};

const initialFiles = {
  bankPassbookCopy: null,
  panCopy: null,
  aadhaarCopy: null,
  experienceLetter: null,
};

const OnBoardingModule = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [files, setFiles] = useState(initialFiles);
  const [errors, setErrors] = useState({});
  const [ifscStatus, setIfscStatus] = useState("idle");
  const [ifscDetails, setIfscDetails] = useState(null);
  const [ifscError, setIfscError] = useState("");
  const [masterData, setMasterData] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const res = await axios.get(`${backendUrl}/hrms/master-data`);

        if (res.data.success) {
          setMasterData(res.data);
        }
      } catch (error) {
        console.error("Master data fetch error:", error);

        if (error.response) {
          alert(error.response.data.message || "Failed to fetch master data.");
        } else {
          alert("Unable to connect to the server.");
        }
      }
    };

    fetchMasterData();
  }, []);

 const handleChange = (event) => {
   const { name, value } = event.target;

   if (
     (name === "experienceYears" || name === "experienceMonths") &&
     value.length > 2
   ) {
     return;
   }

   setFormData((previous) => ({
     ...previous,
     [name]: value,
   }));

   setErrors((previous) => {
     const updatedErrors = {
       ...previous,
       [name]: "",
     };

     if (name === "panNumber" && value.trim()) {
       if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value.trim().toUpperCase())) {
         updatedErrors.panNumber = "Enter a valid PAN number.";
       }
     }

     if (name === "aadhaarNumber" && value.trim()) {
       if (!/^\d{12}$/.test(value.trim())) {
         updatedErrors.aadhaarNumber = "Aadhaar number must contain 12 digits.";
       }
     }

     return updatedErrors;
   });
 };

  const handleFileChange = (event) => {
    const { name, files: selectedFiles } = event.target;
    const file = selectedFiles[0] || null;

    setFiles((previous) => ({
      ...previous,
      [name]: file,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));
  };

  const handleRemoveFile = (name) => {
    setFiles((prev) => ({
      ...prev,
      [name]: null,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      setErrors((previous) => ({
        ...previous,
        profilePicture: "Please upload a JPG or PNG image.",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((previous) => ({
        ...previous,
        profilePicture: "Profile picture must be less than 5 MB.",
      }));
      return;
    }

    setProfilePicture(file);
    setProfilePreview(URL.createObjectURL(file));

    setErrors((previous) => ({
      ...previous,
      profilePicture: "",
    }));
  };

  const handleDeleteProfilePicture = () => {
    setProfilePicture(null);
    setProfilePreview(null);

    setErrors((previous) => ({
      ...previous,
      profilePicture: "",
    }));

    const input = document.getElementById("profilePicture");

    if (input) {
      input.value = "";
    }
  };

  const checkIFSC = async () => {
    const ifsc = formData.ifscCode.trim().toUpperCase();

    if (!ifsc) {
      setIfscError("Please enter an IFSC code");
      setIfscDetails(null);
      setIfscStatus("error");
      return;
    }

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      setIfscError("Please enter a valid IFSC code");
      setIfscDetails(null);
      setIfscStatus("error");
      return;
    }

    try {
      setIfscStatus("loading");
      setIfscError("");
      setErrors((previous) => ({
        ...previous,
        ifscCode: "",
      }));
      setIfscDetails(null);

      const response = await axios.get(`https://ifsc.razorpay.com/${ifsc}`);

      setIfscDetails(response.data);

      setFormData((previous) => ({
        ...previous,
        ifscCode: ifsc,
        bankName: response.data.BANK || "",
      }));

      setIfscStatus("success");
    } catch (error) {
      setIfscDetails(null);
      setIfscError("Invalid IFSC code.");
      setIfscStatus("error");
    }
  };

  const editIFSC = () => {
    setIfscStatus("idle");
    setIfscDetails(null);
    setIfscError("");

    setErrors((previous) => ({
      ...previous,
      ifscCode: "",
    }));
  };

  const validateForm = (formData, files) => {
    const errors = {};

    if (!profilePicture) {
      errors.profilePicture = "Profile picture is required.";
    }

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required.";
    } else if (!/^[A-Za-z\s]+$/.test(formData.firstName.trim())) {
      errors.firstName = "First name should contain only letters.";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required.";
    } else if (!/^[A-Za-z\s]+$/.test(formData.lastName.trim())) {
      errors.lastName = "Last name should contain only letters.";
    }

    if (!formData.fatherName.trim()) {
      errors.fatherName = "Father name is required.";
    } else if (!/^[A-Za-z\s]+$/.test(formData.fatherName.trim())) {
      errors.fatherName = "Father name should contain only letters.";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      errors.mobileNumber = "Enter a valid 10-digit mobile number.";
    }

    if (!formData.gender) {
      errors.gender = "Please select gender.";
    }

    if (!formData.dateOfBirth) {
      errors.dateOfBirth = "Date of birth is required.";
    } else if (new Date(formData.dateOfBirth) > new Date()) {
      errors.dateOfBirth = "Date of birth cannot be in the future.";
    }

    if (
      formData.dateOfBirth &&
      new Date(formData.dateOfJoining) < new Date(formData.dateOfBirth)
    ) {
      errors.dateOfJoining = "Date of joining cannot be before date of birth.";
    }

    if (!formData.bloodGroup) {
      errors.bloodGroup = "Blood Group is required.";
    } else if (!/^(A|B|AB|O)[+-]$/.test(formData.bloodGroup)) {
      errors.bloodGroup = "Invlaid blood group";
    }

    if (!/^[6-9]\d{9}$/.test(formData.emergencyContactNumber)) {
      errors.emergencyContactNumber = "Enter a valid 10-digit contact number.";
    }

    if (!formData.emergencyContactPersonName.trim()) {
      errors.emergencyContactPersonName = "Emergency contact name is required.";
    } else if (
      !/^[A-Za-z\s]+$/.test(formData.emergencyContactPersonName.trim())
    ) {
      errors.emergencyContactPersonName = "Name should contain only letters.";
    }

    if (!formData.emergencyContactPersonRelation) {
      errors.emergencyContactPersonRelation = "Please select relation.";
    }

    if (!formData.permanentAddress.trim()) {
      errors.permanentAddress = "Permanent address is required.";
    }

    if (!formData.communicationAddress.trim()) {
      errors.communicationAddress = "Communication address is required.";
    }

    if (!formData.bankAccountNumber.trim()) {
      errors.bankAccountNumber = "Bank account number is required.";
    } else if (!/^\d{9,18}$/.test(formData.bankAccountNumber)) {
      errors.bankAccountNumber = "Enter a valid bank account number.";
    }

    if (!formData.bankName.trim()) {
      errors.bankName = "Bank name is required.";
    } else if (!/^[A-Za-z\s.&'-]+$/.test(formData.bankName.trim())) {
      errors.bankName = "Enter a valid bank name.";
    }

    if (!formData.ifscCode.trim()) {
      errors.ifscCode = "IFSC code is required.";
    } else if (
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.trim().toUpperCase())
    ) {
      errors.ifscCode = "Enter a valid IFSC code.";
    } else if (ifscStatus !== "success") {
      errors.ifscCode = "Please verify the IFSC code.";
    } else {
      setIfscError("");
    }

    if (formData.uanNumber && !/^\d{12}$/.test(formData.uanNumber)) {
      errors.uanNumber = "UAN must contain 12 digits.";
    }

    if (formData.esicNumber && !/^\d+$/.test(formData.esicNumber)) {
      errors.esicNumber = "ESIC number should contain only digits.";
    }

    if (!formData.panNumber.trim()) {
      errors.panNumber = "PAN Number is required.";
    } else if (
      formData.panNumber &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.panNumber.trim().toUpperCase())
    ) {
      errors.panNumber = "Enter a valid PAN number.";
    }

    if (!formData.aadhaarNumber.trim()) {
      errors.aadhaarNumber = "Aadhaar Number is required.";
    } else if (
      formData.aadhaarNumber &&
      !/^\d{12}$/.test(formData.aadhaarNumber)
    ) {
      errors.aadhaarNumber = "Aadhaar number must contain 12 digits.";
    }

    const years = Number(formData.experienceYears || 0);
    const months = Number(formData.experienceMonths || 0);

    if (
      formData.experienceYears === "" ||
      !/^\d{1,2}$/.test(String(formData.experienceYears)) ||
      years < 0 ||
      years > 99
    ) {
      errors.experienceYears = "Years must be between 0 and 99.";
    }

    if (
      formData.experienceMonths === "" ||
      !/^\d{1,2}$/.test(String(formData.experienceMonths)) ||
      months < 0 ||
      months > 11
    ) {
      errors.experienceMonths = "Months must be between 0 and 11.";
    }

    const hasExperience = years > 0 || months > 0;

    if (hasExperience && !files.experienceLetter) {
      errors.experienceLetter = "Kindly upload the experience Letter.";
    }

    if (files["aadhaarCopy"] == null) {
      errors.aadhaarCopy = "Kindly upload your aadhaar card.";
    }

    if (files["panCopy"] == null) {
      errors.panCopy = "Kindly upload your pan card.";
    }

    if (files["bankPassbookCopy"] == null) {
      errors.bankPassbookCopy = "Kindly upload your bank passbook.";
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    Object.entries(files).forEach(([name, file]) => {
      if (!file) return;

      if (!allowedTypes.includes(file.type)) {
        errors[name] = "Only PDF, JPG and PNG files are allowed.";
      }

      if (file.size > 5 * 1024 * 1024) {
        errors[name] = "File size must be less than 5 MB.";
      }
    });

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm(formData, files);

    setErrors(validationErrors);

    const firstError = Object.keys(validationErrors)[0];

    if (firstError) {
      setTimeout(() => {
        const element = document.querySelector(`[name="${firstError}"]`);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          element.focus();
        }
      }, 0);

      return;
    }

    if (ifscError) return;

    setIsSubmitting(true);

    const payload = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      payload.append(key, value ?? "");
    });

    if (files.bankPassbookCopy) {
      payload.append("bankPassbookCopy", files.bankPassbookCopy);
    }

    if (files.panCopy) {
      payload.append("panCopy", files.panCopy);
    }

    if (files.aadhaarCopy) {
      payload.append("aadhaarCopy", files.aadhaarCopy);
    }

    if (files.experienceLetter) {
      payload.append("experienceLetter", files.experienceLetter);
    }

    if (profilePicture) {
      payload.append("profilePicture", profilePicture);
    }

    try {
      const res = await axios.post(`${backendUrl}/employee/add`, payload);

      if (res.data.success) {
        alert("Employee added successfully.");
      }
    } catch (error) {
      console.error("Add employee error:", error);

      if (error.response) {
        const responseData = error.response.data;

        if (responseData.errors) {
          setErrors((previous) => ({
            ...previous,
            ...responseData.errors,
          }));

          const firstBackendError = Object.keys(responseData.errors)[0];

          if (firstBackendError) {
            setTimeout(() => {
              const element = document.querySelector(
                `[name="${firstBackendError}"]`,
              );

              if (element) {
                element.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });

                element.focus();
              }
            }, 0);
          }

          return;
        }

        alert(responseData.message || "Failed to add employee.");
      } else {
        alert("Unable to connect to the server.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setFiles(initialFiles);
    setErrors({});
  };

  return (
    <div className="employee-page">
      <div className="employee-card">
        <header className="employee-header">
          <div>
            <span className="employee-badge">HR MANAGEMENT</span>

            <h1>Employee Onboarding</h1>

            <p>
              Add employee information, employment details and required
              documents.
            </p>
          </div>
        </header>

        <form onSubmit={handleSubmit} noValidate>
          <section className="form-section">
            <SectionHeader
              number="01"
              title="Personal Information"
              description="Basic information about the employee"
            />

            <div className="profile-picture-section">
              {" "}
              <div className="profile-picture-preview">
                {" "}
                {profilePreview ? (
                  <img src={profilePreview} alt="Profile preview" />
                ) : (
                  <div className="profile-picture-placeholder">
                    {" "}
                    <span>👤</span>{" "}
                  </div>
                )}{" "}
              </div>{" "}
              <FileUpload
                name="profilePicture"
                title="Profile Picture"
                file={profilePicture}
                onChange={handleProfilePictureChange}
                onRemove={handleDeleteProfilePicture}
                required
                accept=".jpg,.jpeg,.png"
                fileTypesText="JPG, JPEG or PNG"
                error={errors.profilePicture}
              />{" "}
            </div>

            <div className="form-grid">
              <InputField
                label="First Name"
                name="firstName"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={handleChange}
                required
                error={errors.firstName}
              />

              <InputField
                label="Last Name"
                name="lastName"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={handleChange}
                required
                error={errors.lastName}
              />

              <InputField
                label="Father Name"
                name="fatherName"
                placeholder="Enter father's name"
                value={formData.fatherName}
                onChange={handleChange}
                required
                error={errors.fatherName}
              />

              <InputField
                label="Email ID"
                name="email"
                type="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                error={errors.email}
              />

              <InputField
                label="Mobile Number"
                name="mobileNumber"
                type="tel"
                placeholder="Enter mobile number"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
                error={errors.mobileNumber}
              />

              <div className="form-group">
                <label>
                  Gender <span>*</span>
                </label>

                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="gender"
                      value="Male"
                      checked={formData.gender === "Male"}
                      onChange={handleChange}
                      required
                    />
                    <span>Male</span>
                  </label>

                  <label className="radio-option">
                    <input
                      type="radio"
                      name="gender"
                      value="Female"
                      checked={formData.gender === "Female"}
                      onChange={handleChange}
                    />
                    <span>Female</span>
                  </label>
                </div>

                {errors.gender && (
                  <small className="error-message">{errors.gender}</small>
                )}
              </div>

              <InputField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                error={errors.dateOfBirth}
              />

              <InputField
                label="Date of Joining"
                name="dateOfJoining"
                type="date"
                value={formData.dateOfJoining}
                onChange={handleChange}
                error={errors.dateOfJoining}
              />

              <SelectField
                label="Marital Status"
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleChange}
                options={["Single", "Married", "Divorced", "Widowed"]}
              />

              <InputField
                label="Blood Group"
                name="bloodGroup"
                placeholder="e.g. O+"
                value={formData.bloodGroup}
                required
                onChange={handleChange}
                error={errors.bloodGroup}
              />
            </div>
          </section>

          <section className="form-section">
            <SectionHeader
              number="02"
              title="Employment Details"
              description="Job, department and organizational information"
            />

            <div className="form-grid">
              <SelectField
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                error={errors.department}
                options={masterData.departments}
              />
              <SelectField
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                error={errors.designation}
                options={masterData.designations}
              />
              <SelectField
                label="Reporting Manager"
                name="reportingManager"
                value={formData.reportingManager}
                onChange={handleChange}
                error={errors.reportingManager}
                options={masterData.managers}
              />
              <div className="experience-group">
                <label>Total Experience</label>

                <div className="experience-fields">
                  <div className="experience-item">
                    <InputField
                      name="experienceYears"
                      type="number"
                      placeholder="0"
                      value={formData.experienceYears}
                      onChange={handleChange}
                      error={errors.experienceYears}
                    />
                    <span>Years</span>
                  </div>

                  <div className="experience-item">
                    <InputField
                      name="experienceMonths"
                      type="number"
                      placeholder="0"
                      value={formData.experienceMonths}
                      onChange={handleChange}
                      error={errors.experienceMonths}
                    />
                    <span>Months</span>
                  </div>
                </div>
              </div>
              <InputField
                label="Work Location"
                name="workLocation"
                placeholder="Enter work location"
                value={formData.workLocation}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="form-section">
            <SectionHeader
              number="03"
              title="Contact Information"
              description="Contact and emergency contact details"
            />

            <div className="form-grid">
              <InputField
                label="Emergency Contact Number"
                name="emergencyContactNumber"
                type="tel"
                placeholder="Enter emergency contact number"
                value={formData.emergencyContactNumber}
                onChange={handleChange}
                required
                error={errors.emergencyContactNumber}
              />

              <InputField
                label="Emergency Contact Person Name"
                name="emergencyContactPersonName"
                placeholder="Enter contact person's name"
                value={formData.emergencyContactPersonName}
                onChange={handleChange}
                required
                error={errors.emergencyContactPersonName}
              />

              <SelectField
                label="Emergency Contact Person Relation"
                name="emergencyContactPersonRelation"
                value={formData.emergencyContactPersonRelation}
                onChange={handleChange}
                required
                error={errors.emergencyContactPersonRelation}
                options={[
                  "Father",
                  "Mother",
                  "Spouse",
                  "Brother",
                  "Sister",
                  "Son",
                  "Daughter",
                  "Guardian",
                  "Friend",
                  "Other",
                ]}
              />

              <TextareaField
                label="Permanent Address"
                name="permanentAddress"
                placeholder="Enter permanent address"
                value={formData.permanentAddress}
                onChange={handleChange}
                fullWidth
                required
                error={errors.permanentAddress}
              />

              <TextareaField
                label="Communication Address"
                name="communicationAddress"
                placeholder="Enter communication address"
                value={formData.communicationAddress}
                onChange={handleChange}
                fullWidth
                required
                error={errors.communicationAddress}
              />
            </div>
          </section>

          <section className="form-section">
            <SectionHeader
              number="04"
              title="Bank Details"
              description="Salary account and statutory information"
            />

            <div className="form-grid">
              <InputField
                label="Bank Account Number"
                name="bankAccountNumber"
                placeholder="Enter account number"
                value={formData.bankAccountNumber}
                onChange={handleChange}
                required
                error={errors.bankAccountNumber}
              />

              <InputField
                label="UAN Number"
                name="uanNumber"
                placeholder="Enter UAN number"
                value={formData.uanNumber}
                onChange={handleChange}
                error={errors.uanNumber}
              />

              <div className="ifsc-group">
                <div className="ifsc-input-row">
                  <InputField
                    label="IFSC Code"
                    name="ifscCode"
                    placeholder="e.g. SBIN0001234"
                    value={formData.ifscCode}
                    onChange={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                      handleChange(e);
                      setIfscDetails(null);
                      setIfscError("");
                      setIfscStatus("idle");
                    }}
                    disabled={ifscStatus === "success"}
                    required
                    error={ifscError || errors.ifscCode}
                  />

                  {ifscStatus === "success" && (
                    <button
                      type="button"
                      className="ifsc-edit-button"
                      onClick={editIFSC}
                      aria-label="Edit IFSC code"
                    >
                      <img src={editIcon} alt="" />
                    </button>
                  )}

                  <button
                    type="button"
                    className={`ifsc-check-button ${ifscStatus}`}
                    onClick={checkIFSC}
                    disabled={
                      ifscStatus === "loading" || ifscStatus === "success"
                    }
                    aria-label={
                      ifscStatus === "success"
                        ? "IFSC verified"
                        : ifscStatus === "loading"
                          ? "Checking IFSC"
                          : "Check IFSC"
                    }
                  >
                    {ifscStatus === "loading" && (
                      <img src={loaderIcon} alt="" />
                    )}

                    {ifscStatus === "success" && <img src={checkIcon} alt="" />}

                    {(ifscStatus === "idle" || ifscStatus === "error") && (
                      <img
                        src={ifscStatus === "error" ? refreshIcon : searchIcon}
                        alt=""
                      />
                    )}
                  </button>
                </div>
              </div>

              <InputField
                label="ESIC Number"
                name="esicNumber"
                placeholder="Enter ESIC number"
                value={formData.esicNumber}
                onChange={handleChange}
                hint="if applicable"
                error={errors.esicNumber}
              />
              {ifscDetails && (
                <div className="ifsc-details-card">
                  <div className="ifsc-details-header">
                    <div className="ifsc-bank-icon">🏦</div>

                    <div>
                      <h3>{ifscDetails.BANK}</h3>
                      <p>{ifscDetails.BRANCH}</p>
                    </div>
                  </div>

                  <div className="ifsc-details-grid">
                    <div>
                      <span>IFSC Code</span>
                      <strong>{ifscDetails.IFSC}</strong>
                    </div>

                    <div>
                      <span>Bank Code</span>
                      <strong>{ifscDetails.BANKCODE}</strong>
                    </div>

                    <div>
                      <span>City</span>
                      <strong>{ifscDetails.CITY}</strong>
                    </div>

                    <div>
                      <span>District</span>
                      <strong>{ifscDetails.DISTRICT}</strong>
                    </div>

                    <div>
                      <span>State</span>
                      <strong>{ifscDetails.STATE}</strong>
                    </div>

                    <div className="ifsc-address">
                      <span>Branch Address</span>
                      <strong>{ifscDetails.ADDRESS}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="form-section">
            <SectionHeader
              number="05"
              title="Identity & Documents"
              description="Government identification and supporting documents"
            />

            <div className="form-grid">
              <InputField
                label="PAN Number"
                name="panNumber"
                placeholder="Enter PAN number"
                value={formData.panNumber}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                  handleChange(e);
                }}
                required
                error={errors.panNumber}
              />

              <InputField
                label="Aadhaar Number"
                name="aadhaarNumber"
                placeholder="Enter Aadhaar number"
                value={formData.aadhaarNumber}
                onChange={handleChange}
                required
                error={errors.aadhaarNumber}
              />
            </div>

            <div className="upload-grid">
              <FileUpload
                name="bankPassbookCopy"
                title="Bank Passbook Copy"
                file={files.bankPassbookCopy}
                onChange={handleFileChange}
                onRemove={handleRemoveFile}
                required
                error={errors.bankPassbookCopy}
              />

              <FileUpload
                name="panCopy"
                title="PAN Copy"
                file={files.panCopy}
                onChange={handleFileChange}
                onRemove={handleRemoveFile}
                required
                error={errors.panCopy}
              />

              <FileUpload
                name="aadhaarCopy"
                title="Aadhaar Copy"
                file={files.aadhaarCopy}
                onChange={handleFileChange}
                onRemove={handleRemoveFile}
                required
                error={errors.aadhaarCopy}
              />

              <FileUpload
                name="experienceLetter"
                title="Experience Letter"
                file={files.experienceLetter}
                onChange={handleFileChange}
                onRemove={handleRemoveFile}
                error={errors.experienceLetter}
              />
            </div>
          </section>

          <div className="form-footer">
            <p>
              <span>*</span>
              Required fields must be completed.
            </p>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                Clear
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                aria-label={
                  isSubmitting ? "Submitting employee" : "Submit employee"
                }
              >
                {isSubmitting ? <>Submitting...</> : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnBoardingModule;
